import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';

export default function HeroSection({ t }) {
  const { language } = useLanguage();
  const [showText, setShowText] = useState(false);
  const navigate = useNavigate();

  const handleStartPlanning = () => {
    navigate('/contact');
  };

  useEffect(() => {
    // Check if user has visited before
    const hasVisited = localStorage.getItem('heroVisited');
    if (hasVisited) {
      setShowText(true);
      return;
    }
    let timeoutId;
    let imageLoaded = false;
    // Show text after 1.5s if image hasn't loaded
    timeoutId = setTimeout(() => {
      if (!imageLoaded) setShowText(true);
    }, 1500);
    // Listen for image load
    const img = document.createElement('img');
    img.src = '/images/beachkiss.jpg';
    img.onload = () => {
      imageLoaded = true;
      setShowText(true);
      localStorage.setItem('heroVisited', 'true');
      clearTimeout(timeoutId);
    };
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="relative h-[32vh] min-h-[120px] max-h-[180px] sm:h-[80vh] sm:min-h-0 sm:max-h-none flex items-center justify-center overflow-hidden pt-0 sm:pt-32 pb-4 sm:pb-0"
    >
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(/images/hero-mobile.jpg)' }}
      >
        <picture>
          <source srcSet="/images/hero-mobile.jpg 400w, /images/beachkiss.jpg 800w, /images/beachkiss.jpg 1600w, /images/beachkiss.jpg 2400w" sizes="(max-width: 600px) 400px, (max-width: 1200px) 800px, (max-width: 1800px) 1600px, 2400px" />
          <img 
            src="/images/hero-mobile.jpg" 
            alt="Hero" 
            className="w-full h-full object-cover object-center" 
            loading="eager"
            width="400"
            height="200"
            fetchpriority="high"
            style={{ display: 'block' }}
          />
        </picture>
        {/* Animated dark overlay for text contrast */}
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 0.6 }}
          transition={{ duration: 1 }}
          className="absolute inset-0 bg-black"
        />
      </div>
      <div className="relative z-10 w-full px-4 pb-4 text-center flex flex-col items-center justify-end mt-14 sm:mt-0">
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: showText ? 1 : 0 }}
          transition={{ duration: 1 }}
          className="text-lg font-bold text-white mb-1 leading-tight sm:text-4xl md:text-6xl sm:mb-8 font-playfair px-6 sm:px-12 md:px-20"
        >
          {language === 'es' ? (
            <>
              <span className="block">Tu Boda de Ensueño en</span>
              <span className="block">México Empieza Aquí</span>
            </>
          ) : (
            <>
              <span className="block">Your Dream Wedding in</span>
              <span className="block">Mexico Starts Here</span>
            </>
          )}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: showText ? 1 : 0 }}
          transition={{ duration: 1 }}
          className="hidden sm:block text-xs text-white/90 sm:text-sm md:text-base max-w-2xl mx-auto font-light drop-shadow-[0_2px_12px_rgba(24,28,36,0.8)]"
        >
          <span className="gold-text">{t.hero.subheadingShort || t.hero.subheading}</span>
        </motion.p>
        <div className="h-2" />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: showText ? 1 : 0 }}
          transition={{ duration: 1 }}
          className="w-full flex flex-col items-center justify-center"
        >
          <button 
            onClick={handleStartPlanning}
            className="bg-[#bfa76a] hover:bg-[#a68d5d] text-white px-8 py-3 rounded-full text-lg font-semibold transition-all text-center justify-center"
          >
            {language === 'es' ? 'Comenzar a Planear' : 'Start Planning'}
          </button>
          <span className="block text-xs text-white/80 mt-1">
            {language === 'es'
              ? 'Sin compromiso. Respuesta en 24h.'
              : 'No obligation. Response within 24h.'}
          </span>
        </motion.div>
        {/* Down arrow/scroll prompt for mobile */}
        <div className="mt-2 animate-bounce text-white/70 text-xs sm:hidden">▼</div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: showText ? 1 : 0 }}
          transition={{ duration: 1 }}
          className="hidden sm:block mt-6 sm:mt-8 text-white/80 text-xs sm:text-sm tracking-wide"
        >
          {t.hero.trusted}
        </motion.div>
      </div>
    </motion.section>
  );
} 